import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import { services } from "@/data/services";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/about", label: "About Dave" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const isHome = loc.pathname === "/";
  // Transparent state only takes effect on MOBILE (< md). Desktop always shows the white glass nav.
  const transparent = isHome && !scrolled && !open;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-neutral-200"
          : transparent
            ? "bg-transparent border-b border-transparent md:bg-white/70 md:backdrop-blur-md"
            : "bg-white/70 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="wrap flex items-center justify-between h-[72px]">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5">
          <img
            src="https://customer-assets.emergentagent.com/job_dave-wraps-studio/artifacts/rzh2dyxf_cropped-928_Logo-281x300.png"
            alt="928 Wraps"
            className={`h-9 md:h-10 w-auto transition-[filter] duration-300 ${transparent ? "brightness-0 invert md:brightness-100 md:invert-0" : ""}`}
          />
          <span className={`hidden sm:inline-block font-mono text-[10px] tracking-[0.3em] pl-2.5 border-l transition-colors duration-300 ${transparent ? "text-white/80 border-white/25 md:text-neutral-500 md:border-neutral-200" : "text-neutral-500 border-neutral-200"}`}>PRESCOTT VALLEY · AZ</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) =>
            l.label === "Services" ? (
              <div key={l.to} className="relative group">
                <NavLink
                  to={l.to}
                  data-testid="nav-services"
                  className={({ isActive }) =>
                    `nav-link text-sm font-semibold text-neutral-800 inline-flex items-center gap-1 ${isActive ? "active" : ""}`
                  }
                >
                  {l.label} <ChevronDown size={14} className="transition-transform duration-200 group-hover:rotate-180" />
                </NavLink>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible translate-y-1 transition-all duration-200 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0">
                  <div data-testid="nav-services-dropdown" className="w-72 bg-white rounded-2xl border border-neutral-200 shadow-xl shadow-black/5 p-2">
                    {services.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        data-testid={`nav-dd-${s.slug}`}
                        className="group/item flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold text-neutral-700 hover:bg-neutral-50 hover:text-black transition-colors"
                      >
                        {s.name}
                        <ArrowRight size={14} className="text-[var(--red)] opacity-0 -translate-x-1 transition-all duration-200 group-hover/item:opacity-100 group-hover/item:translate-x-0" />
                      </Link>
                    ))}
                    <div className="border-t border-neutral-100 mt-1 pt-1">
                      <Link to="/services" data-testid="nav-dd-all" className="flex items-center justify-between px-4 py-2.5 rounded-xl text-xs font-bold tracking-wide uppercase text-[var(--red)] hover:bg-red-50 transition-colors">
                        All services <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `nav-link text-sm font-semibold text-neutral-800 ${isActive ? "active" : ""}`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a href="tel:+19282730784" data-testid="nav-phone" data-track="phone_click" data-track-location="nav" className="text-sm font-semibold text-neutral-700 hover:text-black">
            (928) 273-0784
          </a>
          <Link to="/mockup" data-testid="nav-cta" data-track="cta_click" data-track-location="nav" className="btn-red !py-3 !px-5 text-sm">
            Get My FREE Mockup <ArrowRight size={16} />
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-menu-toggle"
          className={`md:hidden w-10 h-10 grid place-items-center rounded-full border transition-colors duration-300 ${transparent ? "border-white/40 text-white" : "border-neutral-200 text-neutral-800"}`}
          aria-label="Menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-neutral-200 bg-white">
          <div className="wrap py-6 flex flex-col gap-4">
            {links.map((l) => (
              <div key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.end}
                  data-testid={`mnav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-base font-semibold text-neutral-800"
                >
                  {l.label}
                </NavLink>
                {l.label === "Services" && (
                  <div className="mt-3 ml-1 pl-4 border-l-2 border-[var(--red)]/20 flex flex-col gap-2.5">
                    {services.map((s) => (
                      <NavLink
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        data-testid={`mnav-svc-${s.slug}`}
                        className="text-sm font-medium text-neutral-600"
                      >
                        {s.name}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link to="/mockup" data-testid="mnav-cta" className="btn-red mt-2 justify-center">
              Get My FREE Mockup <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
